/**
 * Os arquivos anexados a um conteúdo — a sexta fonte do cadastro, a única que
 * move bytes.
 *
 * *Listar e baixar*: conferidos contra a produção — o conteúdo 100001092 tem dois
 * PNG, e o item 1 voltou com 231.118 bytes de `image/png`. Os bytes não passam por
 * disco nenhum da ApiSistema — quem os guarda é a ApiConteudo, e é ela quem
 * resolve a extensão, porque o banco grava o nome sem ela (`100001092_1`).
 *
 * *Anexar* grava pela ApiConteudo em `httpdocs\dados`, a mesma pasta em que o
 * Sistema antigo grava.
 *
 * *Tirar* apaga o arquivo pela ApiConteudo e a linha de `dado_item`; se apagar o
 * arquivo falhar, a linha sai do mesmo jeito.
 *
 * Em desenvolvimento a trava recusa as duas escritas: conteúdo é dado global.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, obter } from "./cliente"
import { globalCom } from "./conteudo"
import { ErroDaApi } from "./erros"

export type ArquivoDoConteudo = {
  ordem: number
  nome: string
  /** `dado_tipo`. São arquivo os tipos 1, 3, 5 e 6; o resto é referência. */
  tipo: number
  tipoNome: string
}

export function usarArquivosDoConteudo(dadoId: number, baseId: number) {
  return useQuery({
    queryKey: ["conteudo-arquivos", dadoId, baseId] as const,
    enabled: dadoId > 0,
    queryFn: () => obter<ArquivoDoConteudo[]>(`api/conteudos/${dadoId}/arquivos`, { baseId }),
  })
}

/**
 * Baixa um item.
 *
 * `validateStatus` fica aberto de propósito. Com `responseType: "blob"` o corpo
 * do erro chega como Blob, o tradutor de ProblemDetails não o reconhece e a
 * mensagem viraria "não foi possível falar com o servidor" — justamente no caso
 * que mais interessa, o 404 de arquivo cadastrado que sumiu do disco. Lendo o
 * Blob como texto aqui, a explicação da API chega inteira à tela.
 */
export async function baixarArquivoDoConteudo(dadoId: number, ordem: number, baseId: number) {
  const r = await api.get<Blob>(`api/conteudos/${dadoId}/arquivos/${ordem}`, {
    params: { baseId },
    responseType: "blob",
    validateStatus: () => true,
  })

  if (r.status >= 400) throw await erroDeBlob(r.status, r.data)

  const disp = String(r.headers?.["content-disposition"] ?? "")
  const achado = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp)
  const nome = achado ? decodeURIComponent(achado[1]!) : `${dadoId}_${ordem}`

  const url = URL.createObjectURL(r.data)
  const a = document.createElement("a")
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)

  return { nome, bytes: r.data.size, tipo: String(r.headers?.["content-type"] ?? "") }
}

async function erroDeBlob(status: number, corpo: Blob): Promise<ErroDaApi> {
  try {
    const texto = await corpo.text()
    const problema = JSON.parse(texto) as { detail?: string; title?: string }
    return new ErroDaApi(
      problema.detail || problema.title || `Erro ${status}.`, status, [], problema)
  } catch {
    return new ErroDaApi(`Erro ${status} ao baixar o arquivo.`, status, [], null)
  }
}

function invalidar(qc: ReturnType<typeof useQueryClient>, dadoId: number, baseId: number) {
  qc.invalidateQueries({ queryKey: ["conteudo-arquivos", dadoId, baseId] })
  // O arquivo também é um item do conteúdo: a ficha mostra os dois.
  qc.invalidateQueries({ queryKey: ["conteudo", dadoId, baseId] })
}

/**
 * Anexa.
 *
 * O nome do campo é `arquivo`, e a extensão é obrigatória: é dela que sai o
 * `dado_tipo` (`.png` → 6, `.swf` → 1, o resto → 3) e é ela que a ApiConteudo usa
 * para devolver o content-type na leitura.
 */
export function usarEnviarArquivoDoConteudo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      { dadoId, baseId, arquivo }: { dadoId: number; baseId: number; arquivo: File },
    ) => {
      const corpo = new FormData()
      corpo.append("arquivo", arquivo)
      const r = await api.post<ArquivoDoConteudo>(
        `api/conteudos/${dadoId}/arquivos`, corpo, globalCom({ baseId }))
      return r.data
    },
    onSuccess: (_r, v) => invalidar(qc, v.dadoId, v.baseId),
  })
}

/** Tira o item e apaga o arquivo do servidor — ver o cabeçalho. */
export function usarExcluirArquivoDoConteudo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      { dadoId, baseId, ordem }: { dadoId: number; baseId: number; ordem: number },
    ) => {
      await api.delete(`api/conteudos/${dadoId}/arquivos/${ordem}`, globalCom({ baseId }))
    },
    onSuccess: (_r, v) => invalidar(qc, v.dadoId, v.baseId),
  })
}
